import axios from 'axios';

import {
  Contact,
  ContactDto,
  ContactVerificationPreview,
  CreateSubscriptionRequest,
  fromDtoToContact,
  fromDtoToReceivedBroadcast,
  fromDtoToSubscription,
  GetNotificationsParams,
  NotificationApplication,
  NotificationChannel,
  NotificationPage,
  NotificationsProfile,
  ReceivedBroadcast,
  ReceivedBroadcastDto,
  Subscription,
  SubscriptionDto,
  SubscriptionFilter,
  Topic,
  UpdateSubscriptionRequest,
  UserNotification,
} from '@/core/notifications/model';

const BASE = '/api/notifications';

class HttpError extends Error {
  status: number;

  constructor(status: number) {
    super(`Request failed with status ${status}`);
    this.status = status;
  }
}

async function getTopics(): Promise<Topic[]> {
  return (await axios.get<Topic[]>(`${BASE}/catalog/topics`)).data;
}

async function getChannels(): Promise<NotificationChannel[]> {
  return (await axios.get<NotificationChannel[]>(`${BASE}/me/channels`)).data;
}

async function getSubscriptions(): Promise<Subscription[]> {
  const res = await axios.get<SubscriptionDto[]>(`${BASE}/me/subscriptions`);
  return res.data.map(fromDtoToSubscription);
}

async function createSubscription(body: CreateSubscriptionRequest): Promise<Subscription> {
  const res = await axios.post<SubscriptionDto>(`${BASE}/me/subscriptions`, body);
  return fromDtoToSubscription(res.data);
}

async function updateSubscription(
  id: string,
  body: UpdateSubscriptionRequest,
): Promise<Subscription> {
  const res = await axios.patch<SubscriptionDto>(`${BASE}/me/subscriptions/${id}`, body);
  return fromDtoToSubscription(res.data);
}

async function deleteSubscription(id: string): Promise<void> {
  await axios.delete(`${BASE}/me/subscriptions/${id}`);
}

async function getProfile(): Promise<NotificationsProfile> {
  return (await axios.get<NotificationsProfile>(`${BASE}/me/profile`)).data;
}

async function updateProfile(displayName: string): Promise<NotificationsProfile> {
  return (await axios.put<NotificationsProfile>(`${BASE}/me/profile`, { displayName })).data;
}

async function getPushVapidPublicKey(): Promise<string> {
  return (await axios.get<{ publicKey: string }>(`${BASE}/me/channels/push/vapid-public-key`)).data
    .publicKey;
}

async function getContacts(): Promise<Contact[]> {
  const res = await axios.get<ContactDto[]>(`${BASE}/me/contacts`);
  return res.data.map(fromDtoToContact);
}

async function addContact(
  channelKey: string,
  config: Record<string, unknown>,
  label?: string,
): Promise<Contact> {
  const res = await axios.post<ContactDto>(`${BASE}/me/contacts`, {
    channelKey,
    config,
    ...(label ? { label } : {}),
  });
  return fromDtoToContact(res.data);
}

async function setDefaultContact(id: string): Promise<Contact> {
  const res = await axios.patch<ContactDto>(`${BASE}/me/contacts/${id}/default`);
  return fromDtoToContact(res.data);
}

export interface PatchContactRequest {
  config?: Record<string, unknown>;
  subscriptionFilter?: SubscriptionFilter | null;
  // omit to leave unchanged; a string sets it; null clears it back to unset
  label?: string | null;
}

async function patchContact(id: string, body: PatchContactRequest): Promise<Contact> {
  const res = await axios.patch<ContactDto>(`${BASE}/me/contacts/${id}`, body);
  return fromDtoToContact(res.data);
}

async function deleteContact(id: string): Promise<void> {
  await axios.delete(`${BASE}/me/contacts/${id}`);
}

async function getReceivedBroadcasts(): Promise<ReceivedBroadcast[]> {
  const res = await axios.get<ReceivedBroadcastDto[]>(
    `${BASE}/me/broadcast-subscriptions/received`,
  );
  return res.data.map(fromDtoToReceivedBroadcast);
}

async function getSubscription(id: string): Promise<Subscription> {
  const res = await axios.get<SubscriptionDto>(`${BASE}/me/subscriptions/${id}`);
  return fromDtoToSubscription(res.data);
}

async function getNotifications(params?: GetNotificationsParams): Promise<NotificationPage> {
  const data = (await axios.get(`${BASE}/me/notifications`, { params })).data;
  if (Array.isArray(data)) {
    const full = data.length > 0 && data.length === (params?.limit ?? 20);
    return { items: data, nextCursor: full ? data[data.length - 1].id : undefined };
  }
  if (data?.items !== undefined) return data;
  if (data?.content !== undefined) return { items: data.content, nextCursor: data.nextCursor };
  return { items: [], nextCursor: undefined };
}

async function ackNotification(id: string): Promise<UserNotification> {
  return (await axios.post(`${BASE}/me/notifications/${id}/ack`)).data;
}

async function ackAllNotifications(): Promise<void> {
  await axios.post(`${BASE}/me/notifications/ack-all`);
}

async function unackNotification(id: string): Promise<void> {
  await axios.post(`${BASE}/me/notifications/${id}/unack`);
}

async function getApplications(): Promise<NotificationApplication[]> {
  return (await axios.get<NotificationApplication[]>(`${BASE}/me/applications`)).data;
}

// Public, unauthenticated endpoint — bypasses the shared axios instance so no
// stale/absent Authorization header is sent for logged-out users (see src/api/index.ts).
const VERIFY_CONTACT_URL = `${process.env.REACT_APP_SELECT_HOST}${BASE}/verify-contact`;

async function previewContactVerification(token: string): Promise<ContactVerificationPreview> {
  const res = await fetch(`${VERIFY_CONTACT_URL}?token=${encodeURIComponent(token)}`);
  if (!res.ok) throw new HttpError(res.status);
  return res.json();
}

async function confirmContactVerification(token: string): Promise<void> {
  const res = await fetch(VERIFY_CONTACT_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw new HttpError(res.status);
}

const notificationApi = {
  getTopics,
  getChannels,
  getSubscriptions,
  getSubscription,
  createSubscription,
  updateSubscription,
  deleteSubscription,
  getProfile,
  updateProfile,
  getPushVapidPublicKey,
  getContacts,
  addContact,
  setDefaultContact,
  patchContact,
  deleteContact,
  getReceivedBroadcasts,
  getNotifications,
  ackNotification,
  ackAllNotifications,
  unackNotification,
  getApplications,
  previewContactVerification,
  confirmContactVerification,
};

export default notificationApi;
